import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const TutorialContext = createContext();

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within TutorialProvider');
  }
  return context;
};

export const TutorialProvider = ({ children }) => {
  const [activeTutorial, setActiveTutorial] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [tutorialSettings, setTutorialSettings] = useState(() => {
    const saved = localStorage.getItem('tutorial_settings');
    return saved ? JSON.parse(saved) : {
      enabled: true,
      dismissedTutorials: {},
      firstLoginComplete: false
    };
  });

  const startedTutorials = useRef(new Set());

  useEffect(() => {
    localStorage.setItem('tutorial_settings', JSON.stringify(tutorialSettings));
  }, [tutorialSettings]);

  // Check if user is in their first login session
  const isFirstLogin = useCallback(() => {
    // If onboarding hasn't been done yet, this is the first login
    const onboardingDone = localStorage.getItem('nova_onboarding_done');
    if (!onboardingDone) return true;
    // After onboarding, page tutorials trigger during the SAME session only
    // sessionStorage is cleared when browser tab/window closes
    const firstSession = sessionStorage.getItem('first_login_tutorial_session');
    if (firstSession === 'active') return true;
    return false;
  }, []);

  const startTutorial = useCallback((tutorialId, steps) => {
    if (!tutorialSettings.enabled) return;
    if (tutorialSettings.dismissedTutorials[tutorialId]) return;
    if (startedTutorials.current.has(tutorialId)) return;
    // Only auto-start tutorials on first login
    if (!isFirstLogin()) return;

    startedTutorials.current.add(tutorialId);
    setActiveTutorial({ id: tutorialId, steps });
    setCurrentStep(0);
  }, [tutorialSettings, isFirstLogin]);

  // Force-start a tutorial (bypasses first-login check, for manual re-trigger)
  const forceStartTutorial = useCallback((tutorialId, steps) => {
    if (startedTutorials.current.has(tutorialId)) return;
    startedTutorials.current.add(tutorialId);
    setActiveTutorial({ id: tutorialId, steps });
    setCurrentStep(0);
  }, []);

  const nextStep = () => {
    if (activeTutorial && currentStep < activeTutorial.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTutorial();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipTutorial = () => {
    if (activeTutorial) {
      setTutorialSettings(prev => ({
        ...prev,
        dismissedTutorials: {
          ...prev.dismissedTutorials,
          [activeTutorial.id]: true
        }
      }));
    }
    setActiveTutorial(null);
    setCurrentStep(0);
  };

  const completeTutorial = () => {
    if (activeTutorial) {
      setTutorialSettings(prev => ({
        ...prev,
        dismissedTutorials: {
          ...prev.dismissedTutorials,
          [activeTutorial.id]: true
        }
      }));
    }
    setActiveTutorial(null);
    setCurrentStep(0);
  };

  // Mark first login tutorials as complete (call after user has been through first session)
  const markFirstLoginComplete = useCallback(async () => {
    setTutorialSettings(prev => ({
      ...prev,
      firstLoginComplete: true
    }));
    localStorage.removeItem('first_login_pending');
    // Persist to backend so the resume state on next login is correct.
    // Locked May 17, 2026 — Provider Signup Completion-State bug fix.
    // Without this, the walkthrough would re-fire indefinitely until the
    // localStorage cache was warm, and a fresh browser/device wouldn't
    // know the walkthrough was ever completed.
    try {
      const accountId =
        localStorage.getItem('user_id') ||
        (JSON.parse(localStorage.getItem('user_data') || '{}').id);
      if (accountId) {
        const API = process.env.REACT_APP_BACKEND_URL;
        const token =
          localStorage.getItem('access_token') ||
          localStorage.getItem('auth_token') ||
          localStorage.getItem('token');
        await fetch(`${API}/api/auth/mark-first-login-complete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ account_id: accountId }),
        });
      }
    } catch {
      /* network failure — localStorage flag still flipped so the
         current session won't re-fire the walkthrough; the next cold
         load will resync from session-status. */
    }
  }, []);

  const enableAllTutorials = () => {
    setTutorialSettings({
      enabled: true,
      dismissedTutorials: {},
      firstLoginComplete: false
    });
    startedTutorials.current.clear();
  };

  const disableAllTutorials = () => {
    setTutorialSettings(prev => ({
      ...prev,
      enabled: false
    }));
  };

  const resetTutorial = (tutorialId) => {
    setTutorialSettings(prev => {
      const newDismissed = { ...prev.dismissedTutorials };
      delete newDismissed[tutorialId];
      return { ...prev, dismissedTutorials: newDismissed };
    });
    startedTutorials.current.delete(tutorialId);
  };

  const value = {
    activeTutorial,
    currentStep,
    tutorialSettings,
    startTutorial,
    forceStartTutorial,
    nextStep,
    prevStep,
    skipTutorial,
    completeTutorial,
    markFirstLoginComplete,
    enableAllTutorials,
    disableAllTutorials,
    resetTutorial,
    isFirstLogin
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};
